import clsx from 'clsx';
import React from 'react';
import styles from '../RevenueVendorExpenseTable.module.scss';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { CIRCULAR_TXN_KEY, CIRCULAR_TXN_TOOLTIP, NET_KEY, OVERLAP_TAB_CONFIG, OverlapTabConfig } from './overlap.constants';
import SortIcon from '../../../../../../modules/Investor/Deals/components/DealsList/DealsTable/components/SortIcon/SortIcon';
import type { SortConfig } from '../../../../../../utils/constants/commonConstants';

interface TableHeaderProps {
    data: { name: string; key?: string }[] | undefined;
    sortConfig?: SortConfig;
    onSort?: (field: string) => void;
    tabConfig?: OverlapTabConfig;
}

function Headers(props: TableHeaderProps) {
    const { data, sortConfig, onSort, tabConfig = OVERLAP_TAB_CONFIG.revenue } = props;

    const transformMonthYear = (header: string) => {
        const parts = header.split(' ');
        if (parts.length === 2 && !isNaN(Number(parts[1]))) {
            return `${parts[0]}'${parts[1]}`;
        }
        return header;
    };

    const isMonthYear = (header: string) => {
        const parts = header.split(' ');
        return parts.length === 2 && !isNaN(Number(parts[1]));
    };

    const renderLabel = (column: { name: string; key?: string }) => {
        // Total column (both tabs) — relabelled to "Total" with the shared SortIcon
        // (same component + swap_vert icons the Deals table uses).
        if (column.key === tabConfig.totalKey && onSort) {
            return (
                <span className={styles.SortHeaderBtn}>
                    Total
                    <SortIcon field={tabConfig.totalKey} sortConfig={sortConfig} onSort={onSort} alwaysVisible />
                </span>
            );
        }
        // Circular Txn. / Net headers — tooltip on hovering the label itself (no info icon),
        // plus the same shared SortIcon as Total/months.
        if (column.key === CIRCULAR_TXN_KEY || column.key === NET_KEY) {
            const headerTooltip = column.key === CIRCULAR_TXN_KEY ? CIRCULAR_TXN_TOOLTIP : tabConfig.netColumnTooltip;
            return (
                <span className={styles.HeaderWithInfo}>
                    <Tooltip content={headerTooltip} direction="top" usePortal fitContent>
                        <span>{column.name}</span>
                    </Tooltip>
                    {onSort && <SortIcon field={column.key} sortConfig={sortConfig} onSort={onSort} alwaysVisible />}
                </span>
            );
        }
        // Month columns are sortable too — same shared SortIcon as the Total column.
        if (isMonthYear(column.name) && onSort && column.key) {
            return (
                <span className={styles.SortHeaderBtn}>
                    {transformMonthYear(column.name)}
                    <SortIcon field={column.key} sortConfig={sortConfig} onSort={onSort} alwaysVisible />
                </span>
            );
        }
        return transformMonthYear(column.name);
    };

    return (
        <tr className={styles.TableHeadRow}>
            {data
                ? data.map((column, i) => (
                      <th
                          key={i}
                          className={clsx(styles.TableHeadRowItem, {
                              [styles.RightAligned]: isMonthYear(column.name),
                              [styles.TotalCol]: column.key === tabConfig.totalKey,
                              [styles.CircularCol]: column.key === CIRCULAR_TXN_KEY,
                              [styles.NetCol]: column.key === NET_KEY,
                          })}
                      >
                          {renderLabel(column)}
                      </th>
                  ))
                : null}
        </tr>
    );
}

export default Headers;
